import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";

import IconButton from "components/elements/IconButton";
import Table from "components/elements/Table";
import { ReactComponent as EyeIcon } from "../../../images/eye.svg";
import { ReactComponent as ApproveIcon } from "../../../images/approve.svg";
import { ReactComponent as RejectIcon } from "../../../images/reject.svg";
import { ReactComponent as RefreshIcon } from "../../../images/refresh.svg";
import { ReactComponent as AddIcon } from "../../../images/add.svg";
import SubscriptionPreview from "./SubscriptionPreview";
import dataProvider from "core/DataProvider";
import Exporter from "components/elements/Exporter";

import {
  CONTENT_TYPES,
  LS_CLONE_SUB_KEY,
  SUB_STATUSES,
  SUB_STATUSES_REVERSE,
  USER_BUSINESS_ADMIN,
  USER_SUPERADMIN,
} from "core/Constants";
import utility from "core/Utils";
import { toast } from "react-hot-toast";
import Modal from "components/elements/Modal";
import IconInput from "components/elements/IconInput";

const headerMap = {
  actions: "Actions",
  id: "SID",
  client_id: "CID",
  client_name: "Client Name",
  group_name: "Group",
  status: "Status",
  advertisers: "Advertisers",
  content_type: "Content Type",
  period_from: "Period From",
  period_to: "Period To",
};

const exportHeader = [
  { key: "id", label: "SID" },
  { key: "client_id", label: "CID" },
  { key: "client_name", label: "Client Name" },
  { key: "group_name", label: "Group Name" },
  { key: "status", label: "Status" },
  { key: "advertisers", label: "Advertisers" },
  { key: "content_type", label: "Content Type" },
  { key: "period_from", label: "Period From" },
  { key: "period_to", label: "Period To" },
  { key: "created_on", label: "Created On" },
];

const SubscriptionListing = () => {
  const [pendingSubscriptions, setPendingSubscriptions] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [previewData, setPreviewData] = useState({});
  const [isPreview, setIsPreview] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState({});
  const [rejectReason, setRejectReason] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const [isLoader, setIsLoader] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);

  const navigate = useNavigate();
  const token = useOutletContext();

  const previewModal = (
    <SubscriptionPreview
      onClose={(action = null) => {
        setIsPreview(false);
        if (action) {
          if (action === "refresh_listing") {
            initialLoad();
          }
        }
      }}
      data={previewData}
      allowClone={true}
      allowModify={!token.client_type}
    />
  );

  const modal = (
    <Modal onClose={() => setModalVisible(false)}>{modalContent}</Modal>
  );

  const permissionCheck = (key, val = null) => {
    let show = false;

    if (key === "approve_reject_button") {
      // if approved from client side, only barc admin can approve/reject
      if (val == SUB_STATUSES.PARTIAL_APPROVED) {
        if ([USER_BUSINESS_ADMIN, USER_SUPERADMIN].includes(token.type)) {
          show = true;
        }
      }
    }

    if (key === "run_refresh") {
      if ([USER_SUPERADMIN, USER_BUSINESS_ADMIN].includes(token.type)) {
        show = true;
      }
    }

    if (key === "delete_subscription") {
      if ([USER_SUPERADMIN, USER_BUSINESS_ADMIN].includes(token.type)) {
        show = true;
      }
    }

    return show;
  };

  const fetchSubscriptions = (status = [SUB_STATUSES.PENDING]) => {
    return new Promise(async (resolve, reject) => {
      try {
        let data = await dataProvider.network.get(
          "subscriptions?status=" + status.join("&status=")
        );
        for (let i = 0; i < data.length; i++) {
          data[i].content_type = CONTENT_TYPES[data[i].content_type];
          // data[i].period_from = utility.formatDate(data[i].period_from);
          // data[i].period_to = utility.formatDate(data[i].period_to);
          data[i].raw_status = data[i].status;
          data[i].status = SUB_STATUSES_REVERSE[data[i].status];
        }
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  };

  const fetchApprovedSubscriptions = async () => {
    try {
      let data = await fetchSubscriptions([SUB_STATUSES.APPROVED,SUB_STATUSES.CLIENT_REJECTED]);
      for (let i = 0; i < data.length; i++) {
        data[i].actions = (
          <div className="flex">
            <div
              className="action-button flex flex-center"
              title="view"
              onClick={() => fetchSubDetails(data[i].id)}
            >
              <EyeIcon />
            </div>
            {permissionCheck("run_refresh") && !data[i].data_exists && (
              <div
                className="action-button flex flex-center"
                title="refresh subscription"
                onClick={() => {
                  runRefresh(data[i]);
                }}
              >
                {refreshingId === data[i].id && isLoader ? (
                  <div className="loader">...</div>
                ) : (
                  <RefreshIcon />
                )}
              </div>
            )}
            {permissionCheck("delete_subscription") && !data[i].data_exists && (
              <div
                className="action-button flex flex-center"
                title="Delete"
                onClick={() => {
                  deleteSubscription(data[i].id);
                }}
              >
                <AddIcon style={{ transform: "rotate(45deg)" }} />
              </div>
            )}
          </div>
        );
      }
      setSubscriptions(data);
      //console.log(data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch subscriptions");
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingSubscriptions = async () => {
    try {
      let data = await fetchSubscriptions([
        SUB_STATUSES.PENDING,
        SUB_STATUSES.PARTIAL_APPROVED,
      ]);

      for (let i = 0; i < data.length; i++) {
        data[i].actions = (
          <div className="flex">
            <div
              className="action-button flex flex-center"
              title="view"
              onClick={() => fetchSubDetails(data[i].id)}
            >
              <EyeIcon />
            </div>
            {permissionCheck("approve_reject_button", data[i].raw_status) && (
              <div
                className="action-button flex flex-center"
                title="approve"
                onClick={() => confirmApproval(data[i])}
              >
                <ApproveIcon />
              </div>
            )}
            {permissionCheck("approve_reject_button", data[i].raw_status) && (
              <div
                className="action-button flex flex-center"
                title="reject"
                onClick={() => {
                  confirmRejection(data[i]);
                }}
              >
                <RejectIcon />
              </div>
            )}
            {/* {permissionCheck("delete_subscription") && !data[i].data_exists && (
              <div
                className="action-button flex flex-center"
                title="Delete"
                onClick={() => {
                  deleteSubscription(data[i].id);
                }}
              >
                <AddIcon style={{ transform: "rotate(45deg)" }} />
              </div>
            )} */}
          </div>
        );
      }

      setPendingSubscriptions(data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch subscriptions");
    }
  };

  const confirmApproval = sub => {
    let modalContent = (
      <div>
        <p>Are you sure you want to approve this subscription?</p>
        <div className="flex mtop">
          <button
            className="positive"
            onClick={() => {
              setModalVisible(false);
              approveSubscription(sub);
            }}
          >
            Yes
          </button>
          <button
            className="negative"
            onClick={() => {
              setModalVisible(false);
            }}
          >
            No
          </button>
        </div>
      </div>
    );

    setModalContent(modalContent);
    setModalVisible(true);
  };

  const confirmRejection = sub => {
    let modalContent = (
      <div>
        <p>Please provide a reason for rejection</p>
        <div className="flex mtop">
          <textarea
            onChange={e => setRejectReason(e.target.value)}
            placeholder="Reason"
          >
            {rejectReason}
          </textarea>
        </div>
        <div className="flex mtop">
          <button
            className="positive"
            onClick={() => {
              setModalVisible(false);
              rejectSubscription(sub, rejectReason);
            }}
          >
            Submit
          </button>
          <button
            className="negative"
            onClick={() => {
              setModalVisible(false);
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );

    setModalContent(modalContent);
    setModalVisible(true);
  };

  const approveSubscription = async sub => {
    try {
      await dataProvider.network.get("subscriptions/approve?id=" + sub.id);
      fetchApprovedSubscriptions();
      fetchPendingSubscriptions();
      toast.success("Subscription approved successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to approve subscription");
    }
  };

  const runRefresh = async sub => {
    try {
      setIsLoader(true);
      setRefreshingId(sub.id);

      let data = {
        id: String(sub.id),
        period_from: utility.formatDateForInput(sub.period_from),
        period_to: utility.formatDateForInput(sub.period_to),
      };

      await dataProvider.network.post("subscriptions/refresh", data);
      toast.success("Subscription refresh started");

      // refresh list to remove the button
      fetchApprovedSubscriptions();
    } catch (error) {
      console.error(error);
      toast.error("Failed to run subscription refresh");
    }
  };

  const rejectSubscription = (sub, reason) => {
    // rejection API call here
    toast.success("Subscription rejected successfully");
  };

  const deleteSubscription = async id => {
    try {
      await dataProvider.network.get("subscription/" + id);
      initialLoad();
      toast.success("Subscription deleted successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete subscription");
    }
  };

  const fetchSubDetails = async id => {
    try {
      let data = await dataProvider.network.get("subscriptions/full?id=" + id);

      data.advertisers = data.advertisers.map(x => JSON.parse(x));
      data.categories = data.categories.map(x => JSON.parse(x));
      data.brands = data.brands.map(x => JSON.parse(x));
      data.captions = data.captions.map(x => JSON.parse(x));
      data.channels = data.channels.map(x => JSON.parse(x));

      let previewData = {
        id: data.id,
        group: { label: data.group_name, value: data.group_id },
        client: { label: data.client_name, value: data.client_id },
        contentType: { value: data.content_type },
        advertiser: { label: data.advertisers.map(x => x.name).join(",") },
        advertiserList: data.advertisers,
        periodFrom: data.period_from,
        periodTo: data.period_to,
        category: data.categories.map(x => ({ label: x.name })),
        categoryList: data.categories,
        brand: data.brands.map(x => ({ label: x.name })),
        brandList: data.brands,
        caption: data.captions.map(x => ({ label: x.name })),
        captionList: data.captions,
        channels: data.channels.map(x => ({ label: x.name })),
        channelsList: data.channels,
        edit: data.edits.map(x => ({ label: x })),
        user: token,
        sub_status:data.status,
      };

      setPreviewData(previewData);
      setIsPreview(true);
    } catch (error) {
      console.error(error);
      toast.error("Error fetching subscriptions details");
    }
  };

  const initialLoad = () => {
    fetchApprovedSubscriptions();
    fetchPendingSubscriptions();
  };

  useEffect(() => {
    initialLoad();
    console.log("lor", isLoader);
    console.log("refreshingId", refreshingId);
  }, [isLoader, refreshingId]);

  return (
    <section id="subscription-list-container">
      {isPreview && previewModal}
      {modalVisible && modal}
      {(!token.client_id || token.userwise_subscription) && (
        <div className="flex">
          <div className="right flex flex-v-center">
            <IconButton
              icon="add"
              text="New Subscription"
              onClick={_ => {
                localStorage.removeItem(LS_CLONE_SUB_KEY);
                navigate("/subscriptions/new");
              }}
            />
          </div>
        </div>
      )}
      <div className="box mtop">
        <h3 className="pad-lr">Pending Approvals</h3>
        {pendingSubscriptions.length > 0 ? (
          <div className="mtop">
            <Table
              headers={headerMap}
              data={pendingSubscriptions.map(x => ({
                ...x,
                period_from: utility.formatDate(x.period_from),
                period_to: utility.formatDate(x.period_to),
              }))}
            />
          </div>
        ) : (
          <p className="info mtop">
            There are no pending subscriptions to approve
          </p>
        )}
      </div>
      <div className="box mtop pad-1">
        <div className="flex flex-v-center">
          <h3 className="pad-lr">Subscriptions</h3>
          <div className="right flex">
            <div className="el">
              {loading && (
                <div className="loader">
                  <div className="lds-hourglass"></div>
                </div>
              )}
            </div>
            <div className="el pad-lr">
              <IconInput
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search (min 3 character)"
                icon="search"
                style={{ minWidth: "250px" }}
              />
            </div>
            <div
              className="pad-lr"
              style={{ marginRight: "10px", marginTop: "15px" }}
            >
              <Exporter
                format="csv"
                headers={exportHeader}
                data={subscriptions.map(x => ({
                  ...x,
                  period_from: utility.formatDate(x.period_from),
                  period_to: utility.formatDate(x.period_to),
                }))}
                filename="subscription.csv"
                fileID=""
                clientID={null}
              />
            </div>
          </div>
        </div>
        <div className="mtop">
          <Table
            headers={headerMap}
            data={subscriptions.map(x => ({
              ...x,
              period_from: utility.formatDate(x.period_from),
              period_to: utility.formatDate(x.period_to),
            }))}
            search={search}
          />
        </div>
      </div>
    </section>
  );
};

export default SubscriptionListing;
